import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import './ModuleLauncher.css';

const ModuleLauncher = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Module configurations with professional styling
  const moduleConfig = {
    'COLDSTORE': {
      displayName: 'Cold Store',
      letterIcon: 'CS',
      useImage: true,
      imageUrl: '/warehouse-icon.png',  // Make sure this file exists in public folder
      gradient: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
      shadowColor: 'rgba(37, 99, 235, 0.4)',
      subtitle: 'Inventory Management System',
      location: 'BAPATLA',
      division: 'CCC4',
      description: 'Manage shrimp cold storage inventory, track cartons, shipments, and monitor stock levels across all chambers.'
    },
    'PPC': {
      displayName: 'PPC',
      letterIcon: 'PPC',
      useImage: false,
      gradient: 'linear-gradient(135deg, #059669, #047857)',
      shadowColor: 'rgba(5, 150, 105, 0.4)',
      subtitle: 'Contractor Dashboard',
      location: 'BAPATLA',
      division: 'CCC4',
      description: 'Track contractor productivity, manage daily entries, monitor targets, and analyze performance metrics.'
    },
    'EXECUTIVE': {
      displayName: 'Executive',
      letterIcon: '📊',
      useImage: false,
      gradient: 'linear-gradient(135deg, #7c3aed, #5b21b6)',
      shadowColor: 'rgba(124, 58, 237, 0.4)',
      subtitle: 'Executive Dashboard',
      location: 'BAPATLA',
      division: 'CCC4',
      description: 'High-level KPIs, production analytics, real-time metrics, and comprehensive business intelligence for executives.'
    },
    'DEFAULT': {
      displayName: 'Module',
      letterIcon: 'M',
      useImage: false,
      gradient: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
      shadowColor: 'rgba(124, 58, 237, 0.4)',
      subtitle: 'System Module',
      location: '',
      division: '',
      description: 'Access this module to manage your operations.'
    }
  };

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch user's modules on mount
  useEffect(() => {
    fetchUserModules();
  }, []);

  const fetchUserModules = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/modules.php?my_modules=1`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setUser(response.data.user);
        setModules(response.data.modules);

        // If user has only one module, redirect directly
        if (response.data.modules.length === 1) {
          navigate(response.data.modules[0].base_path);
          return;
        }
      } else {
        setError('Failed to load modules');
      }
    } catch (err) {
      console.error('Error fetching modules:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        setError('Failed to connect to server');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleModuleClick = (module) => {
    // Special handling for Executive module - go directly to Executive Dashboard
    if (module.module_code === 'EXECUTIVE') {
      navigate('/dashboard?tab=executive_dashboard');
    } else {
      navigate(module.base_path);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('username');
    navigate('/login');
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit',
      hour12: true 
    });
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getModuleConfig = (module) => {
    return moduleConfig[module.module_code] || moduleConfig['DEFAULT'];
  };

  if (loading) {
    return (
      <div className="launcher-container">
        <div className="launcher-loading">
          <div className="spinner"></div>
          <p>Loading modules...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="launcher-container">
        <div className="launcher-error">
          <div className="error-icon">⚠️</div>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="launcher-container">
      {/* Header */}
      <header className="launcher-header">
        <div className="header-content">
          <div className="launcher-logo">
            <img src="/choice_logo.jpg" alt="Choice Trading Corporation" className="company-logo" />
            <div className="logo-text">
              <span className="logo-title">CHOICE TRADING CORPORATION</span>
              <span className="logo-subtitle">Management System</span>
            </div>
          </div>
          
          <div className="header-right">
            <div className="datetime-section">
              <div className="time-display">{formatTime(currentTime)}</div>
              <div className="date-display">{formatDate(currentTime)}</div>
            </div>
            
            <div className="user-section">
              <div className="user-avatar">{getInitials(user?.full_name)}</div>
              <div className="user-info">
                <span className="user-name">{user?.full_name || 'User'}</span>
                <span className="user-role">{user?.role || 'USER'}</span>
              </div>
              <button className="logout-btn" onClick={handleLogout} title="Logout">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16 17 21 12 16 7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="launcher-main">
        <div className="welcome-section">
          <h1 className="welcome-title">
            Welcome back, <span className="name-highlight">{user?.full_name?.split(' ')[0] || 'User'}</span>!
          </h1>
          <p className="welcome-subtitle">Select a module to continue</p>
        </div>

        {/* Module Cards with Info Below Each */}
        <div className="modules-grid">
          {modules.map((module) => {
            const config = getModuleConfig(module);
            return (
              <div key={module.id} className="module-wrapper">
                {/* Main Card */}
                <div
                  className="module-card"
                  style={{
                    background: config.gradient,
                    boxShadow: `0 10px 40px ${config.shadowColor}`
                  }}
                  onClick={() => handleModuleClick(module)}
                >
                  {config.useImage ? (
                    <div className="card-icon-img">
                      <img 
                        src={config.imageUrl} 
                        alt={config.displayName}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = config.letterIcon;
                          e.target.parentElement.classList.add('card-icon-fallback');
                        }}
                      />
                    </div>
                  ) : (
                    <div className="card-icon">{config.letterIcon}</div>
                  )}
                  <div className="card-content">
                    <h3 className="card-title">{config.displayName}</h3>
                    <p className="card-subtitle">{config.subtitle}</p>
                    {config.location && (
                      <div className="card-location-info">
                        <span className="location-badge">
                          <span className="location-icon">📍</span> {config.location}
                        </span>
                        <span className="division-badge">
                          <span className="division-icon">🏢</span> {config.division}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="card-arrow">→</div>
                </div>
                
                {/* Info Card Below */}
                <div className="info-card" onClick={() => handleModuleClick(module)}>
                  <p className="info-description">{config.description}</p>
                  <button 
                    className="launch-btn"
                    style={{ color: module.module_code === 'COLDSTORE' ? '#2563eb' : module.module_code === 'PPC' ? '#059669' : module.module_code === 'EXECUTIVE' ? '#7c3aed' : '#6366f1' }}
                  >
                    Launch Module →
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Quick Stats */}
        <div className="quick-stats">
          <div className="stat-item">
            <span className="stat-icon">📊</span>
            <span className="stat-text">System Status: <strong className="status-online">Online</strong></span>
          </div>
          <div className="stat-item">
            <span className="stat-icon">📦</span>
            <span className="stat-text">Modules Available: <strong>{modules.length}</strong></span>
          </div>
          <div className="stat-item">
            <span className="stat-icon">👤</span>
            <span className="stat-text">Role: <strong>{user?.role || 'User'}</strong></span>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="launcher-footer">
        <p>© 2025 Choice Group. All rights reserved.</p>
        <p className="version">Version 2.0.0</p>
      </footer>
    </div>
  );
};

export default ModuleLauncher;